import React, { useCallback, useEffect, useState } from 'react';
import Button from '../../components/common/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Toast from '../../components/common/Toast';
import MarkdownView from '../../components/common/MarkdownView';
import {
  CompetitionContent, CompetitionRole, ContentKind, errorMessage, errorStatus, fetchContent, saveContent,
} from '../../services/manageApi';
import MarkdownEditor from './MarkdownEditor';
import ReadOnlyField from './ReadOnlyField';

interface Props {
  competitionId: number;
  kind: ContentKind;
  title: string;
  help: string;
  canEdit: boolean;
  requiredRole?: CompetitionRole;
  onDirtyChange: (section: string, dirty: boolean) => void;
  onSaved?: (content: CompetitionContent) => void;
}

const MarkdownContentSection: React.FC<Props> = ({
  competitionId, kind, title, help, canEdit, requiredRole, onDirtyChange, onSaved,
}) => {
  const section = `content-${kind}`;
  const [content, setContent] = useState<CompetitionContent | null>(null);
  const [draft, setDraft] = useState('');
  const [loadError, setLoadError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');

  const load = useCallback(async () => {
    setLoadError('');
    try {
      const result = await fetchContent(competitionId, kind);
      setContent(result);
      setDraft(result.markdown);
    } catch (err) {
      setContent(null);
      setLoadError(errorStatus(err) === 409
        ? 'This competition has no asset folder yet. Ask a platform admin to set one up.'
        : errorMessage(err, `Couldn't load ${title.toLowerCase()}`));
    }
  }, [competitionId, kind, title]);

  useEffect(() => {
    load();
  }, [load]);

  const dirty = content !== null && draft !== content.markdown;

  useEffect(() => {
    onDirtyChange(section, dirty);
  }, [section, dirty, onDirtyChange]);

  const save = async () => {
    setSaving(true);
    setSaveError('');
    try {
      const result = await saveContent(competitionId, kind, draft);
      setContent(result);
      setDraft(result.markdown);
      setToast(`${title} saved`);
      onSaved?.(result);
    } catch (err) {
      setSaveError(errorMessage(err, `Failed to save ${title.toLowerCase()}`));
    } finally {
      setSaving(false);
    }
  };

  let body: React.ReactNode;
  if (loadError) {
    body = (
      <div className="manage-error" role="alert">
        {loadError}{' '}
        <Button variant="ghost" size="sm" onClick={load}>Retry</Button>
      </div>
    );
  } else if (!content) {
    body = <LoadingSpinner size="sm" text={`Loading ${title.toLowerCase()}...`} />;
  } else if (canEdit) {
    body = (
      <>
        <MarkdownEditor
          id={`md-${kind}`}
          label={`${title} (Markdown)`}
          value={draft}
          onChange={setDraft}
          disabled={saving}
          downloadName={kind === 'readme' ? 'Readme.md' : 'Evaluation.md'}
        />
        {!content.exists && <p className="manage-field__help">No file yet. Saving creates it.</p>}
        {saveError && <p className="manage-error" role="alert">{saveError}</p>}
        <div className="manage-actions">
          <Button variant="secondary" disabled={!dirty || saving} onClick={() => { setDraft(content.markdown); setSaveError(''); }}>
            Discard
          </Button>
          <Button variant="primary" loading={saving} disabled={!dirty || saving} onClick={save}>
            Save {title.toLowerCase()}
          </Button>
        </div>
      </>
    );
  } else {
    body = (
      <ReadOnlyField
        label={title}
        value={content.markdown ? <div className="manage-markdown"><MarkdownView>{content.markdown}</MarkdownView></div> : ''}
        requiredRole={requiredRole}
      />
    );
  }

  return (
    <section className="manage-section" data-tour={`manage-markdown-${kind}`}>
      <h3>{title}</h3>
      <p className="manage-field__help">{help}</p>
      {body}
      {toast && <Toast message={toast} duration={2500} onClose={() => setToast('')} />}
    </section>
  );
};

export default MarkdownContentSection;
